import { useContext } from 'react'

import type { AppDispatch } from '@redux/store'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import mergeAndStringifyEntityObjects from '@functions/mergeAndStringifyEntityObjects'
import { getRelatedEntityOwnerOrSubject } from '@functions/relatedEntity'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as relatedEntityActions from '@redux/modules/relatedEntity'

import defaultFormState, { requiredFields } from '@models/relatedEntity'

import type { DeleteParams } from '@redux/modules/types'
import type { RelatedEntityModel, RelatedEntityRequestOptions } from '@models/types'

const stringifyObjectKeys = ['data']

type CreateRelatedEntityParams = {
  dispatch: AppDispatch,
  relatedEntityParams: Partial<RelatedEntityModel>,
  requestOptions?: RelatedEntityRequestOptions,
}

const createRelatedEntity = (params: CreateRelatedEntityParams) => {
  const { relatedEntityParams, dispatch, requestOptions } = params
  const { createRelatedEntity: createFn } = relatedEntityActions

  // @ts-expect-error TS2345
  const updatedParams = mergeAndStringifyEntityObjects({}, relatedEntityParams, stringifyObjectKeys)

  return dispatch(createFn(updatedParams, requestOptions))
}

type UpdateRelatedEntityParams = {
  dispatch: AppDispatch,
  relatedEntity: RelatedEntityModel,
  relatedEntityParams: Partial<RelatedEntityModel>,
  requestOptions?: RelatedEntityRequestOptions,
}

const updateRelatedEntity = (params: UpdateRelatedEntityParams) => {
  const {
    dispatch, relatedEntity, relatedEntityParams, requestOptions,
  } = params
  const { updateRelatedEntity: updateFn } = relatedEntityActions

  const updatedParams = mergeAndStringifyEntityObjects({
    id: relatedEntity.id,
  // @ts-expect-error TS2345
  }, relatedEntityParams, stringifyObjectKeys)

  // @ts-expect-error TS2345
  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteRelatedEntityParams = {
  dispatch: AppDispatch,
  // @ts-expect-error TS2344
  relatedEntity: DeleteParams<RelatedEntityModel>,
}

const deleteRelatedEntity = (params: DeleteRelatedEntityParams) => {
  const { relatedEntity, dispatch } = params
  const { deleteRelatedEntity: deleteFn } = relatedEntityActions

  return dispatch(deleteFn(relatedEntity))
}

// @ts-expect-error TS7006
const createOrEditRelatedEntity = params => new Promise((resolve, reject) => {
  const { relatedEntity, showCreateOrEditRelatedEntityModal } = params

  if (showCreateOrEditRelatedEntityModal){
    const payload = {
      relatedEntity,
    }

    showCreateOrEditRelatedEntityModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateOrEditRelatedEntityModal not defined in PageContext callbacks'))
})

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useRelatedEntityForm(
  relatedEntity: Partial<RelatedEntityModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const relatedEntityForm = useForm(
    defaultFormState,
    { entity: relatedEntity, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [relatedEntity.id, relatedEntity.cache_key],
  )

  return {
    ...relatedEntityForm,
  }
}

// Return related entities
export const useRelations = (
  relatedEntity: Partial<RelatedEntityModel>,
) => {
  const {
    data = {}, owner_id, owner_type, provider_id, service_product_id, subject_id, subject_type, template_page_id,
  } = relatedEntity

  const entities = useSelector(reduxState => reduxState.entities)
  const { providers, serviceProducts, templatePages } = entities

  const provider = provider_id && providers[provider_id] ? providers[provider_id] : {}
  const serviceProduct = service_product_id && serviceProducts[service_product_id]
    ? serviceProducts[service_product_id]
    : {}
  const templatePage = template_page_id && templatePages[template_page_id] ? templatePages[template_page_id] : {}

  const owner = getRelatedEntityOwnerOrSubject({
    entities,
    filters: Object.keys(data || {}).reduce((acc, key) => {
      // @ts-expect-error TS7053
      acc[`data.${key}`] = data[key]
      return acc
    }, {}),
    // @ts-expect-error TS2322
    item: { id: subject_id, type: subject_type },
    relatedEntity,
    returnType: 'owner',
    ownerType: owner_type,
  })

  const subject = getRelatedEntityOwnerOrSubject({
    entities,
    filters: Object.keys(data || {}).reduce((acc, key) => {
      // @ts-expect-error TS7053
      acc[`data.${key}`] = data[key]
      return acc
    }, {}),
    // @ts-expect-error TS2322
    item: { id: owner_id, type: owner_type },
    relatedEntity,
    returnType: 'subject',
    subjectType: subject_type,
  })

  const serviceProductTemplatePages = subject_type === 'ServiceProduct'
    // @ts-expect-error
    ? serviceProduct?.options?.template?.pages ?? []
    : []

  return {
    entities,
    owner,
    provider,
    serviceProduct,
    serviceProductTemplatePages,
    subject,
    templatePage,
  }
}

function useRelatedEntity(initEntity: Partial<RelatedEntityModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: relatedEntity }: { entity: RelatedEntityModel} = useLatestEntity(initEntity, 'relatedEntities')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)
  const { showCreateOrEditRelatedEntityModal } = callbacks || {}

  const { creating, loading, updating } = useSelector(reduxState => reduxState.relatedEntities)

  return {
    callbacks: {
      createOrEditRelatedEntity: () => (
        createOrEditRelatedEntity({
          relatedEntity,
          showCreateOrEditRelatedEntityModal,
        })
      ),
      createRelatedEntity: (
        relatedEntityParams: Partial<RelatedEntityModel>,
        entityRequestOptions?: RelatedEntityRequestOptions,
      ) => (
        createRelatedEntity({ relatedEntityParams, dispatch, requestOptions: entityRequestOptions })
      ),
      deleteRelatedEntity: (entityRequestOptions?: RelatedEntityRequestOptions) => (
        // @ts-expect-error TS2322
        deleteRelatedEntity({ dispatch, relatedEntity, requestOptions: entityRequestOptions })
      ),
      updateRelatedEntity: (
        relatedEntityParams: Partial<RelatedEntityModel>,
        entityRequestOptions?: RelatedEntityRequestOptions,
      ) => (
        updateRelatedEntity({
          relatedEntity, relatedEntityParams, dispatch, requestOptions: entityRequestOptions,
        })
      ),
    },
    creating,
    loading,
    relatedEntity,
    updating,
  }
}

export default useRelatedEntity
